import {Button, Link, Stack, Tooltip, Typography} from '@mui/material';
import type {GridColDef} from '@mui/x-data-grid-premium';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import {centered, emptyCell, subLine} from '~/components/data-grid/cells/gridCells';
import {Tag} from '~/components/tag/Tag';
import type {TagColor} from '~/components/tag/tag.styles';
import {MONO_FONT} from '~/style/fonts';
import type {ExploreRow} from '../domain/exploreTree';
import {confidenceLabel, riskExplanation} from '../domain/riskScore';
import {RiskExplanation} from './RiskExplanation';
import type {Risk, RiskScore, RiskSummary} from '../types/categoryProfileTypes';

export const RISK_LABEL: Record<Risk, string> = {high: 'High risk', medium: 'Medium risk', low: 'Low risk', insufficient: 'Too little data'};
const RISK_COLOR: Record<Risk, TagColor> = {high: 'error', medium: 'warning', low: 'success', insufficient: 'default'};

/** The tag, with a note under it when the level was set by the team or rests on part of the data. */
function riskBadge(risk: Risk, summary?: Pick<RiskSummary, 'confidence' | 'byTeam'> | null) {
    const partial = summary && !summary.byTeam && risk !== 'insufficient' && summary.confidence !== 'full';
    return (
        <Stack alignItems="flex-start" gap={0.25}>
            <Tag size="small" color={RISK_COLOR[risk]} label={RISK_LABEL[risk]}
                 variant={risk === 'insufficient' ? 'outlined' : 'filled'}/>
            {(partial || summary?.byTeam) && <Typography variant="caption" color="text.secondary" sx={{lineHeight: 1}}>
                {summary.byTeam ? 'Set by the team' : confidenceLabel(summary.confidence)}
            </Typography>}
        </Stack>
    );
}

/** The risk level as a tag; with a score, its explanation as the tooltip and a note when it rests on part of the data. */
export function riskTag(risk: Risk, tooltip?: string, score?: RiskScore | null) {
    const lines = score ? riskExplanation(score) : tooltip ? [tooltip] : [];
    const tag = riskBadge(risk, score && {confidence: score.confidence, byTeam: Boolean(score.override?.level)});
    return lines.length
        ? <Tooltip title={<Stack gap={0.5}>{lines.map(l => <span key={l}>{l}</span>)}</Stack>}><span>{tag}</span></Tooltip>
        : tag;
}

/** A tree row's risk tag: the explanation (one category's score) is fetched when the tooltip opens. */
function exploreRiskTag(row: ExploreRow, databagId: number, fallback: string) {
    const tag = riskBadge(row.risk, row.riskSummary);
    return row.riskSummary
        ? <Tooltip title={<RiskExplanation databagId={databagId} path={row.path} fallback={fallback}/>}><span>{tag}</span></Tooltip>
        : <Tooltip title={fallback}><span>{tag}</span></Tooltip>;
}

export function yoyTag(yoyPct: number | null) {
    if (yoyPct === null) return emptyCell();
    const up = yoyPct >= 0;
    return centered(
        <Tooltip title="Spend change against the same months a year earlier">
            <Typography variant="body2" sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5,
                color: up ? 'warning.main' : 'success.main'}}>
                {up ? <TrendingUpIcon fontSize="small"/> : <TrendingDownIcon fontSize="small"/>}
                {`${up ? '+' : ''}${yoyPct.toFixed(1)}%`}
            </Typography>
        </Tooltip>,
    );
}

type Options = {
    databagId: number;
    money: (v: number) => string;
    onViewDetails: (row: ExploreRow) => void;
    /** In tree mode the grid's grouping column shows the name; in the flat level view this column does. */
    withNameColumn: boolean;
};

/** Columns of the Explore Categories grid (spec 3.6). The category name is the tree's grouping column. */
export function getExploreColumns({databagId, money, onViewDetails, withNameColumn}: Options): GridColDef<ExploreRow>[] {
    const name: GridColDef<ExploreRow> = {
        field: 'name', headerName: 'Category', width: 280,
        renderCell: ({row}) => centered(<>
            <Link component="button" underline="hover" color="inherit" variant="body2" noWrap title={`Open ${row.name}`}
                  onClick={e => {
                      e.stopPropagation();
                      onViewDetails(row);
                  }}
                  sx={{fontWeight: 500, textAlign: 'left', alignSelf: 'flex-start', maxWidth: '100%'}}>
                {row.name}
            </Link>
            {categoryCellCaption(row)}
        </>),
    };
    return [
        ...(withNameColumn ? [name] : []),
        {
            field: 'code', headerName: 'ID', width: 90,
            renderCell: ({row}) => row.code
                ? centered(<Typography variant="body2" sx={{fontFamily: MONO_FONT}}>{row.code}</Typography>)
                : emptyCell(),
        },
        {
            field: 'spend', headerName: 'Total spend', type: 'number', width: 120,
            valueFormatter: (v: number) => money(v),
        },
        {
            field: 'spendSharePct', headerName: 'Spend %', type: 'number', width: 90,
            valueFormatter: (v: number) => `${v.toFixed(1)}%`,
        },
        {field: 'suppliers', headerName: 'Suppliers', type: 'number', width: 100},
        {field: 'transactions', headerName: 'Transactions', type: 'number', width: 120},
        {
            field: 'yoyPct', headerName: 'YoY', width: 100,
            renderCell: ({row}) => yoyTag(row.yoyPct),
        },
        {
            field: 'risk', headerName: 'Risk', width: 130, type: 'singleSelect',
            valueOptions: ['high', 'medium', 'low', 'insufficient'],
            renderCell: ({row}) => centered(exploreRiskTag(row, databagId,
                `Ranked by spend per supplier among its siblings (${money(row.spendPerSupplier)} per supplier)`)),
        },
        {
            field: 'actions', headerName: '', width: 120, sortable: false, filterable: false, align: 'right',
            disableExport: true,
            renderCell: ({row}) => centered(
                <Button size="small" variant="text" onClick={e => {
                    e.stopPropagation();
                    onViewDetails(row);
                }}>
                    View details
                </Button>,
            ),
        },
    ];
}

export function categoryCellCaption(row: ExploreRow) {
    if (row.rank && row.parentPath) return subLine(row.parentPath);
    const n = row.children.length;
    return subLine(n ? `${n} sub-categor${n === 1 ? 'y' : 'ies'}` : 'No sub-categories');
}
